import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePersistStore } from "../../store";
import { DASHBOARD_TYPE } from "../../utils/enums";
import { getDashboardForPath } from "../../utils/dashboard-routes";
import Avatar from "../Avatar";
import NotificationsPopup from "../NotificationsPopup";

interface HeaderProps {
  onHamburgerClick: () => void;
}

interface ModeSwitchProps {
  isSeller: boolean;
  onSelect: (seller: boolean) => void;
  className?: string;
}

const ModeSwitch: React.FC<ModeSwitchProps> = ({
  isSeller,
  onSelect,
  className = "",
}) => (
  <div
    className={`dashModeSwitch ${className}`}
    role="tablist"
    aria-label="Dashboard mode"
  >
    <button
      type="button"
      role="tab"
      aria-selected={!isSeller}
      className={`dashModeSwitch__opt ${!isSeller ? "is-active" : ""}`}
      onClick={() => onSelect(false)}
    >
      Buyer
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={isSeller}
      className={`dashModeSwitch__opt ${isSeller ? "is-active" : ""}`}
      onClick={() => onSelect(true)}
    >
      Seller
    </button>
  </div>
);

const DashboardHeader: React.FC<HeaderProps> = ({ onHamburgerClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, dashboardType, setDashboardType } = usePersistStore(
    (s: any) => s
  );

  const isSeller = dashboardType === DASHBOARD_TYPE.SELLER;
  // Strip a trailing slash so `/dashboard/` matches `/dashboard`.
  const onDashboardHome =
    location.pathname.replace(/\/$/, "") === "/dashboard";

  const handleChange = (seller: boolean) => {
    setDashboardType(seller ? DASHBOARD_TYPE.SELLER : DASHBOARD_TYPE.BUYER);
    // Shared screens (help, profile, dashboard home) render for both modes,
    // so switch in place. Buyer/seller-only screens have no counterpart in
    // the other mode — and Layout would flip the mode straight back — so
    // those still go to the dashboard home.
    if (getDashboardForPath(location.pathname) !== "shared") {
      navigate("/dashboard");
    }
  };

  return (
    <header className="dashHeader">
      <div className="dashHeader__row">
        <button
          type="button"
          className="dashHeader__hamburger d-xl-none"
          onClick={onHamburgerClick}
          aria-label="Open menu"
        >
          <i className="bi bi-list" aria-hidden="true" />
        </button>

        <div className="dashHeader__greeting">
          <span className="dashHeader__hi">Welcome back,</span>
          <span className="dashHeader__name" title={user?.name}>
            {user?.name}
          </span>
          <span className="dashHeader__wave" aria-hidden="true">
            👋
          </span>
        </div>

        <div className="dashHeader__actions">
          <ModeSwitch
            isSeller={isSeller}
            onSelect={handleChange}
            className="d-none d-md-inline-flex"
          />
          <NotificationsPopup />
          <Link
            to="/dashboard"
            aria-label="Dashboard"
            className="dashHeader__avatar"
            // When already on /dashboard, swallow the click. React Router
            // still pushes a new history entry for same-URL navigations,
            // which re-renders Layout → Buyer/Seller. Those parents define
            // BuyerYearlyBids / BidsList as inner components, so a fresh
            // function reference on each render makes React unmount and
            // remount them — firing their reload useEffects and resending
            // yearly-bids / mine to the API. Skipping the navigation
            // breaks that chain.
            onClick={(e) => {
              if (onDashboardHome) e.preventDefault();
            }}
          >
            <Avatar
              src={user?.fileUrl}
              name={user?.name}
              className="dashHeader__avatarImg"
            />
          </Link>
        </div>
      </div>

      <ModeSwitch
        isSeller={isSeller}
        onSelect={handleChange}
        className="dashModeSwitch--mobile d-md-none"
      />
    </header>
  );
};

export default DashboardHeader;
